import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { BatchToggleNotificationParams, NotificationsResponse, ToggleNotificationParams } from './api';
import type { NotificationWithReadState } from './types';
import { fetchNotifications, markAllNotificationsAsRead, toggleMultipleNotificationReadStates, toggleNotificationReadState } from './api';
import { extractPrNumber } from '@/lib/notifications/hooks/use-notification-aggregation';

/**
 * Query key factory for notifications
 */
export const notificationQueryKey = (userId: string | undefined) => ['notifications', userId] as const;

/**
 * React Query hook for fetching notifications with infinite scrolling
 *
 * @param userId Optional user ID to include in the query key, defaults to 'current-user'
 * @param limit Number of notifications to fetch per page
 * @param enabled Whether the query should fetch notifications
 */
export function useNotifications(userId, t0, t1) {
  const $ = _c(9);
  const limit = t0 === undefined ? 20 : t0;
  const enabled = t1 === undefined ? Boolean(userId) : t1;
  let t2;
  if ($[0] !== userId) {
    t2 = notificationQueryKey(userId);
    $[0] = userId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== limit || $[3] !== userId) {
    t3 = t4 => {
      const {
        pageParam
      } = t4;
      return fetchNotifications(userId, pageParam as string | undefined, limit);
    };
    $[2] = limit;
    $[3] = userId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = Boolean(userId) && enabled;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      initialPageParam: undefined as string | undefined,
      getNextPageParam: _temp,
      enabled: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return useInfiniteQuery(t5);
}

/**
 * React Query hook for updating a notification's read state
 *
 * Supports:
 * - Marking as read (markAsRead = true)
 * - Marking as unread (markAsRead = false)
 *
 * @param userId Optional user ID to invalidate queries for, defaults to 'current-user'
 */
function _temp(lastPage) {
  return lastPage.nextCursor;
}
export function useToggleNotificationReadState(userId) {
  const $ = _c(3);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient || $[1] !== userId) {
    t0 = {
      mutationFn: _temp2,
      onMutate: async t1 => {
        const {
          notificationId,
          markAsRead
        } = t1;
        if (!userId) {
          return;
        }
        await queryClient.cancelQueries({
          queryKey: notificationQueryKey(userId)
        });
        const previousData = queryClient.getQueryData(notificationQueryKey(userId));
        if (previousData) {
          queryClient.setQueryData(notificationQueryKey(userId), {
            ...previousData,
            pages: previousData.pages.map(page => ({
              ...page,
              notifications: page.notifications.map(notification => {
                if (notification.id === notificationId) {
                  return {
                    ...notification,
                    isRead: markAsRead,
                    readAt: markAsRead ? new Date().toISOString() : null,
                    deliveryStatus: markAsRead ? "read" : "delivered"
                  };
                }
                if ("childNotifications" in notification && notification.childNotifications) {
                  const updatedChildren = notification.childNotifications.map(child => {
                    if (child.id === notificationId) {
                      return {
                        ...child,
                        isRead: markAsRead,
                        readAt: markAsRead ? new Date().toISOString() : null,
                        deliveryStatus: markAsRead ? "read" : "delivered"
                      };
                    }
                    return child;
                  });
                  return {
                    ...notification,
                    childNotifications: updatedChildren
                  };
                }
                return notification;
              })
            }))
          });
        }
        return {
          previousData
        };
      },
      onError: (err, _variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(notificationQueryKey(userId), context.previousData);
        }
        console.error("Error updating notification read state:", err);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: notificationQueryKey(userId)
        });
      }
    };
    $[0] = queryClient;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}

/**
 * React Query hook for marking a notification as read
 *
 * @deprecated Use useToggleNotificationReadState instead
 * @param userId Optional user ID to invalidate queries for, defaults to 'current-user'
 */
function _temp2(params) {
  return toggleNotificationReadState(params.notificationId, params.markAsRead);
}
export function useMarkNotificationAsRead(userId) {
  const $ = _c(7);
  const toggleMutation = useToggleNotificationReadState(userId);
  let t0;
  let t1;
  if ($[0] !== toggleMutation) {
    t0 = notificationId => toggleMutation.mutate({
      notificationId,
      markAsRead: true
    });
    t1 = async notificationId_0 => await toggleMutation.mutateAsync({
      notificationId: notificationId_0,
      markAsRead: true
    });
    $[0] = toggleMutation;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== toggleMutation) {
    t2 = {
      ...toggleMutation,
      mutate: t0,
      mutateAsync: t1
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = toggleMutation;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/**
 * React Query hook for updating multiple notifications' read state at once
 *
 * @param userId Optional user ID to invalidate queries for, defaults to 'current-user'
 */
export function useBatchToggleNotificationReadState(userId) {
  const $ = _c(3);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient || $[1] !== userId) {
    t0 = {
      mutationFn: _temp3,
      onMutate: async t1 => {
        const {
          notificationIds,
          markAsRead
        } = t1;
        if (!userId) {
          return;
        }
        await queryClient.cancelQueries({
          queryKey: notificationQueryKey(userId)
        });
        const previousData = queryClient.getQueryData(notificationQueryKey(userId));
        if (previousData) {
          queryClient.setQueryData(notificationQueryKey(userId), {
            ...previousData,
            pages: previousData.pages.map(page => ({
              ...page,
              notifications: page.notifications.map(notification => {
                if (notificationIds.includes(notification.id)) {
                  return {
                    ...notification,
                    isRead: markAsRead,
                    readAt: markAsRead ? new Date().toISOString() : null,
                    deliveryStatus: markAsRead ? "read" : "delivered"
                  };
                }
                if ("childNotifications" in notification && notification.childNotifications) {
                  const hasTargetChild = notification.childNotifications.some(child => notificationIds.includes(child.id));
                  if (hasTargetChild) {
                    const updatedChildren = notification.childNotifications.map(child_0 => {
                      if (notificationIds.includes(child_0.id)) {
                        return {
                          ...child_0,
                          isRead: markAsRead,
                          readAt: markAsRead ? new Date().toISOString() : null,
                          deliveryStatus: markAsRead ? "read" : "delivered"
                        };
                      }
                      return child_0;
                    });
                    return {
                      ...notification,
                      childNotifications: updatedChildren
                    };
                  }
                }
                return notification;
              })
            }))
          });
        }
        return {
          previousData
        };
      },
      onError: (err, _variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(notificationQueryKey(userId), context.previousData);
        }
        console.error("Error updating notification read states:", err);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: notificationQueryKey(userId)
        });
      }
    };
    $[0] = queryClient;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}

/**
 * React Query hook for marking all user's notifications as read
 *
 * @param userId Optional user ID to invalidate queries for, defaults to 'current-user'
 */
function _temp3(params) {
  return toggleMultipleNotificationReadStates(params.notificationIds, params.markAsRead);
}
export function useMarkAllNotificationsAsRead(userId) {
  const $ = _c(3);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient || $[1] !== userId) {
    t0 = {
      mutationFn: markAllNotificationsAsRead,
      onMutate: async () => {
        if (!userId) {
          return;
        }
        await queryClient.cancelQueries({
          queryKey: notificationQueryKey(userId)
        });
        const previousData = queryClient.getQueryData(notificationQueryKey(userId));
        if (previousData) {
          queryClient.setQueryData(notificationQueryKey(userId), {
            ...previousData,
            pages: previousData.pages.map(_temp6)
          });
        }
        return {
          previousData
        };
      },
      onError: (err, _variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(notificationQueryKey(userId), context.previousData);
        }
        console.error("Error marking all notifications as read:", err);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: notificationQueryKey(userId)
        });
      }
    };
    $[0] = queryClient;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}

/**
 * React Query hook for marking all notifications for a specific PR as read
 *
 * @param userId Optional user ID to invalidate queries for, defaults to 'current-user'
 * @param prNumber The PR number to mark notifications for
 */
function _temp6(page) {
  return {
    ...page,
    notifications: page.notifications.map(_temp5)
  };
}
function _temp5(notification) {
  return {
    ...notification,
    isRead: true,
    readAt: new Date().toISOString(),
    deliveryStatus: "read",
    ...("childNotifications" in notification && notification.childNotifications ? {
      childNotifications: notification.childNotifications.map(_temp4)
    } : {})
  };
}
function _temp4(child) {
  return {
    ...child,
    isRead: true,
    readAt: new Date().toISOString(),
    deliveryStatus: "read"
  };
}
export function useMarkPrNotificationsAsRead() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp9,
      onMutate: async t1 => {
        const {
          userId,
          prNumber
        } = t1;
        await queryClient.cancelQueries({
          queryKey: notificationQueryKey(userId)
        });
        const previousData = queryClient.getQueryData(notificationQueryKey(userId));
        if (previousData) {
          queryClient.setQueryData(notificationQueryKey(userId), {
            ...previousData,
            pages: previousData.pages.map(page => ({
              ...page,
              notifications: page.notifications.map(notification_0 => {
                const notificationPrNumber = extractPrNumber(notification_0);
                if (notificationPrNumber === prNumber && !notification_0.isRead) {
                  return {
                    ...notification_0,
                    isRead: true,
                    readAt: new Date().toISOString(),
                    deliveryStatus: "read",
                    ...("childNotifications" in notification_0 && notification_0.childNotifications ? {
                      childNotifications: notification_0.childNotifications.map(_temp10)
                    } : {})
                  };
                }
                return notification_0;
              })
            }))
          });
        }
        return {
          previousData
        };
      },
      onError: (err, t2, context) => {
        const {
          userId: userId_0
        } = t2;
        if (context?.previousData) {
          queryClient.setQueryData(notificationQueryKey(userId_0), context.previousData);
        }
        console.error("Error marking PR notifications as read:", err);
      },
      onSettled: (_, __, t3) => {
        const {
          userId: userId_1
        } = t3;
        queryClient.invalidateQueries({
          queryKey: notificationQueryKey(userId_1)
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp10(child_0) {
  return {
    ...child_0,
    isRead: true,
    readAt: new Date().toISOString(),
    deliveryStatus: "read"
  };
}
async function _temp9(t0) {
  const {
    prNotifications
  } = t0;
  const notificationIds = prNotifications?.flatMap(_temp8);
  const response = await fetch("/api/notifications", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      notificationIds,
      markAsRead: true
    })
  });
  if (!response.ok) {
    throw new Error("Failed to mark PR notifications as read");
  }
  const data = await response.json();
  return data.updatedCount || 0;
}
function _temp8(notification) {
  const ids = [notification.id];
  if ("childNotifications" in notification && notification.childNotifications) {
    notification.childNotifications.filter(_temp7).forEach(child => ids.push(child.id));
  }
  return ids;
}
function _temp7(n) {
  return !n.isRead;
}